import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import MessageBox from '../components/MessageBox';
import PageHeader from '../components/PageHeader';
import api from '../api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const emptyHours = () =>
  DAYS.reduce((acc, day) => {
    acc[day] = { open: '09:00', close: '22:00' };
    return acc;
  }, {});

const normalizeOutlet = outlet => ({
  name: outlet?.name || '',
  description: outlet?.description || '',
  address: outlet?.address || '',
  phone: outlet?.phone || '',
  ownerEmail: outlet?.ownerEmail || '',
  ownerFirstName: outlet?.user?.firstName || '',
  ownerLastName: outlet?.user?.lastName || '',
  cuisineType: outlet?.cuisineType || outlet?.cuisine_type || '',
  imageUrl: outlet?.imageUrl || outlet?.image_url || '',
  isAvailable: outlet?.isAvailable !== false,
  isOpen: outlet?.isOpen !== false,
  isPopular: !!outlet?.isPopular,
  topRated: !!outlet?.topRated,
  openingHours: {
    ...emptyHours(),
    ...(outlet?.openingHours || outlet?.opening_hours || {}),
  },
  coordinates: {
    latitude: outlet?.coordinates?.latitude ?? '',
    longitude: outlet?.coordinates?.longitude ?? '',
  },
});

export default function EditOutlet() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadOutlet = async () => {
      try {
        setLoading(true);
        setMessage(null);
        const response = await api.getRestaurantById(id);
        setFormData(normalizeOutlet(response?.data || response?.data?.data || response));
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.message || 'Outlet not found.',
        });
      } finally {
        setLoading(false);
      }
    };

    loadOutlet();
  }, [id]);

  const hoursPreview = useMemo(() => {
    if (!formData?.openingHours) return '';
    return DAYS.map(day => {
      const hours = formData.openingHours[day];
      return `${day.slice(0, 3)} ${hours?.open || '--:--'}-${hours?.close || '--:--'}`;
    }).join(' | ');
  }, [formData]);

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours?.[day],
          [field]: value,
        },
      },
    }));
  };

  const handleCoordinatesChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      coordinates: {
        ...prev.coordinates,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setMessage(null);

    if (
      !formData.name ||
      !formData.description ||
      !formData.address ||
      !formData.phone ||
      !formData.cuisineType ||
      !formData.ownerEmail ||
      formData.coordinates.latitude === '' ||
      formData.coordinates.longitude === ''
    ) {
      setMessage({ type: 'error', text: 'Please fill all required fields.' });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        description: formData.description,
        address: formData.address,
        phone: formData.phone,
        ownerEmail: formData.ownerEmail,
        ownerFirstName: formData.ownerFirstName,
        ownerLastName: formData.ownerLastName,
        cuisineType: formData.cuisineType,
        isAvailable: formData.isAvailable,
        isOpen: formData.isOpen,
        isPopular: formData.isPopular,
        topRated: formData.topRated,
        openingHours: formData.openingHours,
        coordinates: {
          latitude: Number(formData.coordinates.latitude),
          longitude: Number(formData.coordinates.longitude),
        },
      };

      if (formData.imageUrl.trim()) {
        payload.imageUrl = formData.imageUrl.trim();
      }

      await api.updateRestaurant(id, payload);

      setMessage({ type: 'success', text: 'Outlet updated successfully.' });
      setTimeout(() => navigate('/outlets'), 900);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to update outlet.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <p>Loading outlet...</p>
      </Layout>
    );
  }

  if (!formData) {
    return (
      <Layout>
        {message && <MessageBox type={message.type} message={message.text} onClose={() => setMessage(null)} />}
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        eyebrow="Restaurants"
        title="Edit Outlet"
        subtitle="Update outlet details, image, status, coordinates, and operating hours."
      />

      {message && (
        <MessageBox
          type={message.type}
          message={message.text}
          onClose={() => setMessage(null)}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Outlet Details</h3>
          <div style={styles.grid}>
            <label style={styles.field}>
              <span style={styles.label}>Outlet Name</span>
              <input name="name" value={formData.name} onChange={handleChange} style={styles.input} />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Owner First Name</span>
              <input name="ownerFirstName" value={formData.ownerFirstName} onChange={handleChange} style={styles.input} />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Owner Last Name</span>
              <input name="ownerLastName" value={formData.ownerLastName} onChange={handleChange} style={styles.input} />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Owner Email</span>
              <input name="ownerEmail" value={formData.ownerEmail} onChange={handleChange} style={styles.input} />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Phone Number</span>
              <input name="phone" value={formData.phone} onChange={handleChange} style={styles.input} />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Cuisine Type</span>
              <input name="cuisineType" value={formData.cuisineType} onChange={handleChange} style={styles.input} />
            </label>
          </div>

          <label style={styles.field}>
            <span style={styles.label}>Image URL</span>
            <input name="imageUrl" value={formData.imageUrl} onChange={handleChange} style={styles.input} placeholder="https://example.com/image.jpg" />
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Address</span>
            <input name="address" value={formData.address} onChange={handleChange} style={styles.input} />
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Description</span>
            <textarea name="description" value={formData.description} onChange={handleChange} style={{ ...styles.input, minHeight: 96 }} />
          </label>
        </section>

        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Operating Hours</h3>
          <p style={styles.preview}>{hoursPreview}</p>

          <div style={styles.hoursGrid}>
            {DAYS.map(day => (
              <div key={day} style={styles.dayRow}>
                <strong style={styles.dayLabel}>{day.charAt(0).toUpperCase() + day.slice(1)}</strong>
                <input
                  type="time"
                  value={formData.openingHours[day]?.open || '09:00'}
                  onChange={event => handleOpeningHoursChange(day, 'open', event.target.value)}
                  style={styles.timeInput}
                />
                <span style={styles.toText}>to</span>
                <input
                  type="time"
                  value={formData.openingHours[day]?.close || '22:00'}
                  onChange={event => handleOpeningHoursChange(day, 'close', event.target.value)}
                  style={styles.timeInput}
                />
              </div>
            ))}
          </div>
        </section>

        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Platform Settings</h3>
          <div style={styles.checkGrid}>
            <label style={styles.checkLabel}>
              <input type="checkbox" name="isAvailable" checked={formData.isAvailable} onChange={handleChange} />
              Manually online
            </label>
            <label style={styles.checkLabel}>
              <input type="checkbox" name="isOpen" checked={formData.isOpen} onChange={handleChange} />
              Open now
            </label>
            <label style={styles.checkLabel}>
              <input type="checkbox" name="isPopular" checked={formData.isPopular} onChange={handleChange} />
              Popular
            </label>
            <label style={styles.checkLabel}>
              <input type="checkbox" name="topRated" checked={formData.topRated} onChange={handleChange} />
              Top rated
            </label>
          </div>

          <h3 style={styles.sectionTitle}>Coordinates</h3>
          <div style={styles.grid}>
            <label style={styles.field}>
              <span style={styles.label}>Latitude</span>
              <input
                type="number"
                step="0.0001"
                value={formData.coordinates.latitude}
                onChange={event => handleCoordinatesChange('latitude', event.target.value)}
                style={styles.input}
              />
            </label>
            <label style={styles.field}>
              <span style={styles.label}>Longitude</span>
              <input
                type="number"
                step="0.0001"
                value={formData.coordinates.longitude}
                onChange={event => handleCoordinatesChange('longitude', event.target.value)}
                style={styles.input}
              />
            </label>
          </div>
        </section>

        <div style={styles.actions}>
          <button type="button" style={styles.cancelButton} onClick={() => navigate('/outlets')}>
            Cancel
          </button>
          <button type="submit" style={styles.button} disabled={saving}>
            {saving ? 'Saving...' : 'Update Outlet'}
          </button>
        </div>
      </form>
    </Layout>
  );
}

const styles = {
  form: {
    maxWidth: 980,
    marginTop: 20,
    display: 'grid',
    gap: 18,
  },
  section: {
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: 8,
    padding: 18,
  },
  sectionTitle: {
    margin: '0 0 14px',
    color: '#0F3D2E',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: 14,
  },
  field: {
    display: 'grid',
    gap: 7,
    marginBottom: 14,
  },
  label: {
    fontWeight: 900,
    color: '#0F3D2E',
  },
  input: {
    padding: 11,
    borderRadius: 6,
    border: '1px solid #E6D8BE',
    backgroundColor: '#fff',
    color: '#0F3D2E',
    fontSize: 15,
    width: '100%',
  },
  preview: {
    color: '#746A5B',
    fontWeight: 800,
    marginTop: -4,
  },
  hoursGrid: {
    display: 'grid',
    gap: 10,
  },
  dayRow: {
    display: 'grid',
    gridTemplateColumns: '130px 140px 32px 140px',
    alignItems: 'center',
    gap: 10,
  },
  dayLabel: {
    color: '#0F3D2E',
  },
  timeInput: {
    padding: 10,
    borderRadius: 6,
    border: '1px solid #E6D8BE',
    backgroundColor: '#fff',
    color: '#0F3D2E',
    fontSize: 15,
  },
  toText: {
    color: '#746A5B',
    fontWeight: 800,
  },
  checkGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginBottom: 18,
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#0F3D2E',
    fontWeight: 900,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelButton: {
    padding: '12px 16px',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontWeight: 900,
    border: '1px solid #E6D8BE',
    borderRadius: 6,
    cursor: 'pointer',
  },
  button: {
    padding: '12px 16px',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontWeight: 900,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
};
